/**
 * Tableau de bord WhatsApp pour la secrétaire.
 *
 * Affiche :
 * - Statut de connexion du numéro (Meta WhatsApp Cloud API)
 * - Statistiques du jour (RDV WhatsApp, messages envoyés, sessions actives)
 * - Liste des sessions en cours
 * - Journal des 50 derniers messages
 * - Questions fréquentes du bot (FaqManager)
 * - Configuration du service
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { appointmentsApi } from '../../services/appointmentsApi';
import FaqManager from './FaqManager';
import type { ConfigWhatsApp, MessageWhatsAppLog, SessionWhatsApp } from '../../types/appointments';

// ---- Statut connexion ----

const StatutConnexion: React.FC<{ connecte: boolean; statut: string }> = ({ connecte, statut }) => (
  <div className={`flex items-center gap-2 text-sm font-medium ${connecte ? 'text-green-600' : 'text-red-500'}`}>
    <span className={`w-2.5 h-2.5 rounded-full ${connecte ? 'bg-green-500 animate-pulse' : 'bg-red-400'}`} />
    {connecte ? 'Connecté' : `Déconnecté (${statut})`}
  </div>
);

// ---- Carte statistique ----

const StatCard: React.FC<{ label: string; value: number; icon: React.ReactNode; color: string }> = ({
  label, value, icon, color
}) => (
  <div className={`bg-white rounded-xl border border-gray-100 p-5`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
        <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${color.replace('text-', 'bg-').replace('-600', '-50').replace('-700', '-50')}`}>
        {icon}
      </div>
    </div>
  </div>
);

// ---- Formulaire configuration ----

const ConfigForm: React.FC<{ config: ConfigWhatsApp | null }> = ({ config }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Partial<ConfigWhatsApp>>(config ?? {
    phone_number_id: '',
    waba_id: '',
    template_rappel: '',
    template_langue: 'fr',
    message_bienvenue: '',
    service_actif: false,
    mode_maintenance: false,
    email_alerte: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const inputClass = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-sky-400 focus:outline-none';
  const webhookUrl = new URL(`${api.defaults.baseURL}/appointments/webhook/meta/`, window.location.origin).href;

  const handleSave = async () => {
    setSaving(true);
    try {
      if (config?.id) {
        await appointmentsApi.updateConfig(config.id, form);
      } else {
        await appointmentsApi.saveConfig(form);
      }
      queryClient.invalidateQueries({ queryKey: ['config-whatsapp'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
      <h2 className="font-semibold text-gray-800">Configuration WhatsApp Cloud API (Meta)</h2>
      <p className="text-xs text-gray-500">
        Le jeton d'accès et le secret de l'application restent sur le serveur
        (<code>META_ACCESS_TOKEN</code>, <code>META_APP_SECRET</code>, <code>META_VERIFY_TOKEN</code>).
        URL du webhook à déclarer chez Meta : <code className="select-all">{webhookUrl}</code>
      </p>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Identifiant du numéro (phone_number_id)</label>
          <input type="text" value={form.phone_number_id ?? ''}
            onChange={e => setForm(f => ({ ...f, phone_number_id: e.target.value }))}
            placeholder="123456789012345"
            className={inputClass} />
        </div>
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Identifiant WABA</label>
          <input type="text" value={form.waba_id ?? ''}
            onChange={e => setForm(f => ({ ...f, waba_id: e.target.value }))}
            placeholder="123456789012345"
            className={inputClass} />
        </div>
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Template du rappel J-1</label>
          <input type="text" value={form.template_rappel ?? ''}
            onChange={e => setForm(f => ({ ...f, template_rappel: e.target.value }))}
            placeholder="rappel_rdv"
            className={inputClass} />
        </div>
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Langue du template</label>
          <input type="text" value={form.template_langue ?? ''}
            onChange={e => setForm(f => ({ ...f, template_langue: e.target.value }))}
            placeholder="fr"
            className={inputClass} />
        </div>
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Flow d'inscription (flow_id)</label>
          <input type="text" value={form.flow_inscription_id ?? ''}
            onChange={e => setForm(f => ({ ...f, flow_inscription_id: e.target.value }))}
            placeholder="Vide : l'accueil enregistre les nouveaux patients"
            className={inputClass} />
        </div>
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Adresse du cabinet</label>
          <input type="text" value={form.adresse_cabinet ?? ''}
            onChange={e => setForm(f => ({ ...f, adresse_cabinet: e.target.value }))}
            placeholder=""
            className={inputClass} />
        </div>
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Email d'alerte</label>
          <input type="email" value={form.email_alerte ?? ''}
            onChange={e => setForm(f => ({ ...f, email_alerte: e.target.value }))}
            placeholder="secretariat@cabinet.com"
            className={inputClass} />
        </div>
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Latitude</label>
          <input type="text" value={form.latitude ?? ''}
            onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))}
            placeholder="9.672952"
            className={inputClass} />
        </div>
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Longitude</label>
          <input type="text" value={form.longitude ?? ''}
            onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))}
            placeholder="-13.564010"
            className={inputClass} />
        </div>
      </div>

      <div>
        <label className="text-xs text-gray-600 mb-1 block">Message de bienvenue</label>
        <textarea rows={4} value={form.message_bienvenue ?? ''}
          onChange={e => setForm(f => ({ ...f, message_bienvenue: e.target.value }))}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-sky-400 focus:outline-none resize-none" />
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.service_actif ?? false}
            onChange={e => setForm(f => ({ ...f, service_actif: e.target.checked }))}
            className="rounded text-sky-600" />
          <span className="text-sm text-gray-700">Service actif</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.mode_maintenance ?? false}
            onChange={e => setForm(f => ({ ...f, mode_maintenance: e.target.checked }))}
            className="rounded text-amber-500" />
          <span className="text-sm text-gray-700">Mode maintenance</span>
        </label>
      </div>

      <div className="flex justify-end gap-3">
        {saved && <span className="text-green-600 text-sm self-center">✓ Sauvegardé</span>}
        <button onClick={handleSave} disabled={saving}
          className="bg-sky-600 hover:bg-sky-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50">
          {saving ? 'Sauvegarde...' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
};

// ---- Log messages ----

const LogItem: React.FC<{ log: MessageWhatsAppLog }> = ({ log }) => {
  const isEntrant = log.direction === 'entrant';
  const isEchec = log.statut === 'echec';
  const date = new Date(log.created_at);

  return (
    <div className={`flex gap-3 py-3 border-b border-gray-50 last:border-0 ${isEchec ? 'opacity-60' : ''}`}>
      <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs ${
        isEntrant ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'
      }`}>
        {isEntrant ? '↓' : '↑'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-700">{log.telephone}</span>
          {isEchec && <span className="text-xs bg-red-50 text-red-500 px-1.5 rounded">Échec</span>}
        </div>
        <p className="text-xs text-gray-500 truncate mt-0.5">{log.contenu}</p>
      </div>
      <time className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">
        {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
      </time>
    </div>
  );
};

// ---- Composant principal ----

const WhatsAppDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'apercu' | 'sessions' | 'logs' | 'faq' | 'config'>('apercu');

  const { data: sante, isLoading: santeLoading } = useQuery({
    queryKey: ['sante-whatsapp'],
    queryFn: () => appointmentsApi.getStatutConnexion(),
    refetchInterval: 30000, // Rafraîchir toutes les 30s
  });

  const { data: stats } = useQuery({
    queryKey: ['stats-whatsapp'],
    queryFn: () => appointmentsApi.getStatistiques(),
    refetchInterval: 30000,
    enabled: activeTab === 'apercu',
  });

  const { data: logs = [] } = useQuery({
    queryKey: ['whatsapp-logs'],
    queryFn: () => appointmentsApi.getLogs(),
    refetchInterval: 15000,
    enabled: activeTab === 'logs',
  });

  const queryClient = useQueryClient();
  const { data: sessions = [] } = useQuery({
    queryKey: ['whatsapp-sessions'],
    queryFn: () => appointmentsApi.getSessions(),
    refetchInterval: 15000,
    enabled: activeTab === 'sessions',
  });
  const rendreAuBot = useMutation({
    mutationFn: appointmentsApi.rendreAuBot,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['whatsapp-sessions'] }),
  });

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['config-whatsapp'],
    queryFn: () => appointmentsApi.getConfig(),
    enabled: activeTab === 'config',
  });

  const TABS = [
    { key: 'apercu', label: 'Aperçu' },
    { key: 'sessions', label: 'Sessions actives' },
    { key: 'logs', label: 'Journal messages' },
    { key: 'faq', label: 'Questions fréquentes' },
    { key: 'config', label: 'Configuration' },
  ] as const;

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Tableau de bord WhatsApp</h1>
          <p className="text-sm text-gray-500 mt-0.5">Prise de rendez-vous via WhatsApp Cloud API (Meta)</p>
        </div>
        {sante && (
          <StatutConnexion
            connecte={sante.connecte}
            statut={sante.statut}
          />
        )}
      </div>

      {/* Onglets */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(tab => (
          <button key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeTab === tab.key
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Onglet Aperçu */}
      {activeTab === 'apercu' && (
        <div className="space-y-6">
          {santeLoading ? (
            <div className="h-24 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-sky-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : sante ? (
            <div className="grid grid-cols-3 gap-4">
              <StatCard
                label="Statut connexion"
                value={sante.connecte ? 1 : 0}
                color={sante.connecte ? 'text-green-600' : 'text-red-500'}
                icon={
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                }
              />
            </div>
          ) : null}

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: 'RDV pris par le bot', value: stats.rdv_pris_whatsapp, color: 'text-teal-600' },
                { label: 'Messages reçus', value: stats.messages_recus, color: 'text-blue-600' },
                { label: 'Messages envoyés', value: stats.messages_envoyes, color: 'text-blue-600' },
                { label: "Échecs d'envoi", value: stats.echecs_envoi, color: stats.echecs_envoi ? 'text-red-600' : 'text-gray-500' },
                { label: 'Conversations en cours', value: stats.sessions_actives, color: 'text-sky-600' },
                { label: "À reprendre par l'accueil", value: stats.sessions_accueil, color: stats.sessions_accueil ? 'text-amber-600' : 'text-gray-500' },
              ].map(c => (
                <div key={c.label} className="bg-white rounded-xl border border-gray-100 p-5">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">{c.label}</p>
                  <p className={`text-3xl font-bold mt-1 ${c.color}`}>{c.value}</p>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-400">Activité du jour. Les conversations « à reprendre » attendent une réponse de l'accueil dans l'application WhatsApp Business.</p>
        </div>
      )}

      {/* Onglet Sessions actives */}
      {activeTab === 'sessions' && (
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Conversations des dernières 24 h ({sessions.length})</h2>
          </div>
          {sessions.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm">Aucune session active</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {sessions.map(session => (
                <div key={session.id} className={`px-6 py-4 flex items-center justify-between gap-4 ${
                  session.etape_courante === 'human' ? 'bg-amber-50/60' : ''}`}>
                  <div>
                    <p className="text-sm font-medium text-gray-800">+{session.telephone}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Étape : {session.etape_label}</p>
                  </div>
                  <div className="text-right space-y-1">
                    {session.etape_courante === 'human' ? (
                      <div className="flex items-center gap-2 justify-end">
                        <span className="badge-yellow">Reprise par l'accueil</span>
                        <button
                          onClick={() => rendreAuBot.mutate(session.id)}
                          disabled={rendreAuBot.isLoading}
                          className="text-xs border border-gray-200 text-gray-700 rounded-lg px-2 py-1 hover:bg-white disabled:opacity-50"
                        >
                          Rendre la main au bot
                        </button>
                      </div>
                    ) : session.est_expiree ? (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">Expirée</span>
                    ) : (
                      <span className="text-xs bg-green-50 text-green-600 px-2 py-1 rounded-full">Active</span>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(session.updated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Onglet Journal messages */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">50 derniers messages</h2>
          </div>
          <div className="px-6">
            {logs.length === 0 ? (
              <p className="text-center text-gray-400 py-8 text-sm">Aucun message enregistré</p>
            ) : (
              logs.map(log => <LogItem key={log.id} log={log} />)
            )}
          </div>
        </div>
      )}

      {activeTab === 'faq' && <FaqManager />}

      {/* Onglet Configuration */}
      {activeTab === 'config' && (
        // Le formulaire copie la config dans son état au montage : il ne doit
        // être monté qu'une fois la config reçue (l'API renvoie {} si aucune).
        configLoading
          ? <p className="text-sm text-gray-500">Chargement…</p>
          : <ConfigForm key={config?.id ?? 'nouvelle'} config={config?.id ? config : null} />
      )}
    </div>
  );
};

export default WhatsAppDashboard;
